/** Wire types for the v1 conversation API (`backend/README.md`). */

export interface AdminChatbotSummaryWire {
  id: string;
  name: string;
  description: string | null;
  welcome_message: string | null;
  status: 'draft' | 'published';
  slug: string;
  vector_store_id: string | null;
  kind?: 'subject' | 'assistant' | 'teacher_prep';
  owner_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminChatbotDetailWire extends AdminChatbotSummaryWire {
  system_prompt: string;
}

export interface AdminChatbotListResponse {
  chatbots: AdminChatbotSummaryWire[];
}

export interface AdminVectorStoreSummaryWire {
  id: string;
  name: string;
  description: string | null;
  embedding_model: string | null;
  embedding_dimension: number | null;
  chunk_size: number | null;
  chunk_overlap: number | null;
  default_top_k: number | null;
  visible_to_teachers: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminVectorStoreListResponse {
  vector_stores: AdminVectorStoreSummaryWire[];
}

export interface CreateAdminVectorStoreRequest {
  name: string;
  description?: string | null;
  chunk_size?: number | null;
  chunk_overlap?: number | null;
  default_top_k?: number | null;
  visible_to_teachers?: boolean;
}

export interface UpdateAdminVectorStoreRequest {
  name: string;
  description?: string | null;
  chunk_size?: number | null;
  chunk_overlap?: number | null;
  default_top_k?: number | null;
  visible_to_teachers?: boolean;
}

export interface AdminDocumentListResponse {
  documents: AdminDocumentSummaryWire[];
}

export interface AdminDocumentSummaryWire {
  id: string;
  vector_store_id: string;
  filename: string;
  status: 'processing' | 'ready' | 'failed';
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateAdminChatbotRequest {
  name: string;
  description?: string | null;
  welcome_message?: string | null;
  system_prompt: string;
  vector_store_id?: string | null;
  category_slug?: string | null;
  kind?: 'assistant';
}

/** PATCH excludes `category_slug` — the routing slug is assigned once at
 *  creation and stays stable. */
export type UpdateAdminChatbotRequest = Omit<CreateAdminChatbotRequest, 'category_slug'>;

export interface AdminCategorySummaryWire {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  updated_at: string;
}

export interface AdminCategoryListResponse {
  categories: AdminCategorySummaryWire[];
}

export interface AdminTeacherSummaryWire {
  id: string;
  email: string;
  display_name: string | null;
  created_at: string;
}

export interface AdminTeacherListResponse {
  teachers: AdminTeacherSummaryWire[];
}

export interface CreateAdminCategoryRequest {
  name: string;
}

export type UpdateAdminCategoryRequest = CreateAdminCategoryRequest;

export interface CreateConversationRequest {
  chatbot_slug?: string;
}

export interface ChatbotConfig {
  name: string;
  welcome_message: string;
  max_message_length?: number;
}

export interface ClassroomAssistantSummary {
  slug: string;
  name: string;
  description: string | null;
}

export interface ClassroomAssistantsResponse {
  assistants: ClassroomAssistantSummary[];
}

export interface ClassroomRedeemResponse {
  expires_at: string;
}

export interface TeacherLoginResponse {
  token: string;
  expires_at: string;
}

export interface TeacherProfileResponse {
  id: string;
  email: string;
  display_name: string | null;
  role: string;
  metadata: Record<string, unknown> | null;
}

export interface TeacherInviteValidateResponse {
  email: string;
}

export interface TeacherInviteCreateResponse {
  invite_url: string;
  expires_at: string;
}

export interface ClassroomTokenSummary {
  id: string;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
}

export interface ClassroomTokenCreateResponse {
  id: string;
  code: string;
  expires_at: string;
}

export interface ClassroomTokenListResponse {
  tokens: ClassroomTokenSummary[];
}

export interface CreateConversationResponse {
  id: string;
}

export interface RenameConversationResponse {
  id: string;
  title: string;
}

export interface ConversationSummary {
  id: string;
  title: string | null;
  started_at: string;
  last_message_at: string;
}

/** Teacher prep list/PATCH context responses (student list stays `ConversationSummary`). */
export interface PrepConversationSummary extends ConversationSummary {
  subjects: string[];
  programme: string | null;
  level: string | null;
  active_skill: string | null;
}

export interface ConversationListResponse {
  conversations: ConversationSummary[];
}

export interface PrepConversationListResponse {
  conversations: PrepConversationSummary[];
}

export type BackendMessageRole = 'user' | 'assistant';

export type BackendMessageStatus = 'pending' | 'complete' | 'failed' | 'retracted';

/** Backend message object from history and status poll endpoints. */
export interface BackendMessage {
  id: string;
  role: BackendMessageRole;
  status: BackendMessageStatus;
  content: string;
  /** Explicit UTC ISO 8601 timestamp from the backend. */
  created_at: string;
  guardrail_settled?: boolean;
  replacement_message_id?: string | null;
}

export interface BackendMessageListResponse {
  messages: BackendMessage[];
}

export interface SubmitMessageRequest {
  content: string;
}

export interface SubmitMessageResponse {
  user_message_id: string;
  assistant_message_id: string;
  event_id: string;
  status: 'pending';
}

export interface StreamCompletionDelta {
  role?: string;
  content?: string;
}

export interface StreamCompletionChoice {
  index: number;
  delta: StreamCompletionDelta;
  finish_reason: string | null;
}

export interface StreamCompletionChunk {
  object: 'chat.completion.chunk';
  model: string;
  choices: StreamCompletionChoice[];
}

export interface StreamErrorFrame {
  object: 'error';
  code: 'generation_failed' | 'timeout';
}

export type BackendErrorCode =
  | 'pending_reply'
  | 'conversation_full'
  | 'rate_limited'
  | 'temporarily_blocked'
  | 'not_found'
  | 'chatbot_unavailable'
  | 'dispatch_failed'
  | 'vector_store_not_found'
  | 'chatbot_slug_conflict'
  | 'chatbot_not_found'
  | 'default_chatbot_protected'
  | 'category_not_found'
  | 'category_slug_conflict'
  | 'category_in_use'
  | 'category_slug_reserved'
  | 'unsupported_file_type'
  | 'upload_too_large'
  | 'rag_validation_error'
  | 'document_not_found'
  | 'document_processing'
  | 'vector_store_in_use'
  | 'subject_classify_failed'
  | 'classroom_token_invalid'
  | 'classroom_token_full'
  | 'admin_forbidden'
  | 'teacher_email_conflict'
  | 'teacher_not_found'
  | 'teacher_invite_invalid'
  | 'teacher_invite_config_unavailable'
  | 'classroom_token_limit'
  | 'profile_metadata_invalid'
  | 'classroom_config_unavailable'
  | 'rate_limit_unavailable'
  | 'payment_required'
  | 'skill_not_found'
  | 'skill_not_ready'
  | 'skill_slug_reserved'
  | 'skill_slug_conflict'
  | 'skill_limit'
  | 'stream_slots_exhausted'
  | 'feedback_not_ready'
  | 'feedback_not_found'
  | 'chunk_not_found'
  | 'chunk_empty'
  | 'chunk_too_long'
  | 'embedding_failed';

export interface BackendDomainErrorDetail {
  code: BackendErrorCode;
  message: string;
}

/** Structured domain error for the live adapter (Phase 3). */
export interface ApiErrorShape {
  httpStatus: number;
  code: BackendErrorCode | null;
  userMessage: string;
}

/** Default Danish user-facing messages for documented domain error codes. */
export const DOMAIN_ERROR_MESSAGES_DA: Record<BackendErrorCode, string> = {
  pending_reply: 'Der genereres allerede et svar',
  conversation_full: 'Denne chat har nået beskedgrænsen',
  rate_limited: 'For mange forespørgsler - prøv igen om lidt',
  temporarily_blocked: 'For mange forespørgsler fra dit netværk - prøv igen senere',
  not_found: 'Samtalen findes ikke længere',
  chatbot_unavailable: 'GymGPT er midlertidigt utilgængelig. Prøv igen.',
  dispatch_failed: 'Beskeden kunne ikke sendes. Prøv igen.',
  vector_store_not_found: 'Vector store findes ikke længere.',
  chatbot_slug_conflict: 'Chatbotten kunne ikke oprettes. Prøv igen.',
  chatbot_not_found: 'Chatbotten findes ikke længere.',
  default_chatbot_protected: 'Standard-chatbotten kan ikke slettes.',
  category_not_found: 'Kategorien findes ikke længere.',
  category_slug_conflict: 'Kategorien kunne ikke oprettes. Prøv igen.',
  category_in_use: 'Kategorien kan ikke slettes, fordi den bruges af en chatbot.',
  category_slug_reserved: 'Denne kategori-slug er ikke tilladt.',
  unsupported_file_type: 'Filen skal være .md, .txt, .pdf, .docx eller .json.',
  upload_too_large: 'Filen er for stor.',
  rag_validation_error: 'Handlingen blev afvist. Tjek filen eller vector store-indstillingerne.',
  document_not_found: 'Dokumentet findes ikke længere.',
  document_processing: 'Dokumentet behandles stadig og kan ikke slettes endnu.',
  vector_store_in_use: 'Vector store kan ikke slettes, fordi den bruges af en eller flere chatbots.',
  subject_classify_failed: 'Vi kunne ikke finde det rette fag lige nu. Prøv igen.',
  classroom_token_invalid: 'Ugyldig eller udløbet lærerkode.',
  classroom_token_full: 'Klassen er fuld.',
  admin_forbidden: 'Du har ikke tilladelse til denne handling.',
  teacher_email_conflict: 'Der findes allerede en lærer med den e-mail.',
  teacher_not_found: 'Læreren blev ikke fundet.',
  teacher_invite_invalid: 'Invitationen er ugyldig eller udløbet.',
  teacher_invite_config_unavailable: 'Lærerinvitationer er ikke konfigureret på serveren.',
  classroom_token_limit: 'Du kan ikke have flere aktive klassekoder.',
  profile_metadata_invalid: 'Profiloplysningerne er ugyldige.',
  classroom_config_unavailable: 'Klassekoder er ikke konfigureret på serveren.',
  rate_limit_unavailable: 'Tjenesten er midlertidigt utilgængelig. Prøv igen.',
  payment_required: 'Din lærer-adgang kan ikke sende forberedelsesbeskeder lige nu.',
  skill_not_found: 'Skillen findes ikke.',
  skill_not_ready: 'Skillen er ikke klar endnu.',
  skill_slug_reserved: 'Denne skill er beskyttet eller har et reserveret slug.',
  skill_slug_conflict: 'Der findes allerede en skill med det slug.',
  skill_limit: 'Den maksimale grænse for skills er nået.',
  stream_slots_exhausted: 'For mange aktive svar lige nu. Prøv igen om lidt.',
  feedback_not_ready: 'Svaret er ikke klar endnu.',
  feedback_not_found: 'Feedbacken blev ikke fundet.',
  chunk_not_found: 'Chunken findes ikke længere.',
  chunk_empty: 'Chunken er tom.',
  chunk_too_long: 'Chunken er for lang til at blive søgbar.',
  embedding_failed: 'Teksten kunne ikke lægges ind i søgningen. Intet blev gemt.',
};

export interface PlatformSkillSummaryWire {
  slug: string;
  title: string;
  description: string;
  status: string;
  updated_at: string;
}

export interface PlatformSkillDetailWire extends PlatformSkillSummaryWire {
  id: string;
  system_prompt: string;
  is_protected: boolean;
}

export interface PlatformSkillListResponse {
  skills: PlatformSkillSummaryWire[];
}

export interface PlatformSkillCreateRequest {
  slug: string;
  title: string;
  description?: string;
  system_prompt: string;
}

export interface PlatformSkillPatchRequest {
  slug?: string;
  title?: string;
  description?: string;
  system_prompt?: string;
}

/** Teacher-owned prep skills (`/chat/teacher/prep/skills…`, Slice C). */
export interface OwnedSkillSummaryWire {
  slug: string;
  title: string;
  description: string;
  status: string;
  updated_at: string;
  /**
   * True when a chat proposal awaits **Gem** (also on already-ready skills):
   * a changed prompt, or only a pending title or description. Despite the name
   * it does not mean the prompt itself differs.
   */
  has_pending_prompt?: boolean;
  /** Assistant message whose `propose_custom_skill` call stored this skill. */
  source_message_id?: string | null;
  /** New title a chat revision of a ready skill applies on **Gem**. */
  pending_title?: string | null;
  /** New description a chat revision of a ready skill applies on **Gem**. */
  pending_description?: string | null;
}

export interface OwnedSkillDetailWire extends OwnedSkillSummaryWire {
  system_prompt: string;
  pending_prompt: string;
  vector_store_ids: string[];
  tools: string[];
  artifacts: string[];
  use_subject_store: boolean;
}

export interface OwnedSkillListResponse {
  skills: OwnedSkillSummaryWire[];
}

export interface OwnedSkillSpecsWire {
  max_vector_stores: number;
  tools: string[];
  artifact_formats: string[];
  use_subject_store_default: boolean;
}

export interface OwnedSkillCreateRequest {
  slug: string;
  title: string;
  description?: string;
  system_prompt?: string;
  vector_store_ids?: string[];
  tools?: string[];
  artifacts?: string[];
  use_subject_store?: boolean;
}

export interface OwnedSkillPatchRequest {
  slug?: string;
  title?: string;
  description?: string;
  system_prompt?: string;
}
